import { z } from "zod";
import { RequiredFirestoreTimestampSchema } from "./common.schema";
import { IntegrationStatusSchema } from "./googleAnalytics.schema";

/**
 * Google Search Console site schema
 */
export const GoogleSearchConsoleSiteSchema = z.object({
  site_url: z.string(),
  permission_level: z.string().nullable().optional(),
});

export type GoogleSearchConsoleSite = z.infer<
  typeof GoogleSearchConsoleSiteSchema
>;

/**
 * Google Search Console token reference schema.
 * Raw OAuth tokens live in Secret Manager; Firestore stores secret names.
 */
export const GoogleSearchConsoleTokenRefSchema = z.object({
  access_token_secret: z.string(),
  refresh_token_secret: z.string(),
  expires_at: RequiredFirestoreTimestampSchema,
  scopes: z.array(z.string()),
  token_uri: z.string().default("https://oauth2.googleapis.com/token"),
});

export type GoogleSearchConsoleTokenRef = z.infer<
  typeof GoogleSearchConsoleTokenRefSchema
>;

/**
 * Google Search Console Account schema (Firestore document)
 */
export const GoogleSearchConsoleAccountSchema = z.object({
  account_id: z.string(),
  account_email: z.string().email(),
  account_name: z.string().nullable().default(null),
  token_ref: GoogleSearchConsoleTokenRefSchema.nullable().default(null),
  sites: z.array(GoogleSearchConsoleSiteSchema).default([]),
  client_id: z.string(),
  status: IntegrationStatusSchema.default("connected"),
  error_message: z.string().nullable().default(null),
  created_at: RequiredFirestoreTimestampSchema.nullable().default(null),
  updated_at: RequiredFirestoreTimestampSchema.nullable().default(null),
});

export type GoogleSearchConsoleAccount = z.infer<
  typeof GoogleSearchConsoleAccountSchema
>;

export const parseGoogleSearchConsoleAccount = (
  data: unknown,
): GoogleSearchConsoleAccount => {
  return GoogleSearchConsoleAccountSchema.parse(data);
};

export const safeGoogleSearchConsoleAccountParse = (data: unknown) => {
  return GoogleSearchConsoleAccountSchema.safeParse(data);
};
